import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { formatDate } from "../../../constants/utils";
import {
  adjustDateForTimezone,
  getInitialBookingDates as getInitialBookingDatesHelper,
} from "../utils/targetingConditionsInitial";
import { getEarliestStayStart, startOfDay } from "../utils/dateFieldUtils";

// Counters for generating unique IDs for blackout + stay date rows.
// A monotonic counter (rather than Date.now()) guarantees uniqueness even for
// rows created within the same millisecond — otherwise removing one row would
// filter out every row sharing its id.
let blackoutIdCounter = Date.now();
const getNextBlackoutId = () => ++blackoutIdCounter;

let stayIdCounter = Date.now();
const getNextStayId = () => ++stayIdCounter;

const toAdjustedDate = (dateStr) =>
  dateStr ? adjustDateForTimezone(new Date(dateStr)) : null;

const blankBlackoutRow = () => ({
  id: getNextBlackoutId(),
  dates: null,
  allowStayThrough: true,
});

/**
 * Builds blackout rows from stayVO.blackOutDateRanges; one blank row when the
 * promo has none. Shared by the initial state and the promo re-sync.
 */
const parseBlackoutDates = (ranges) => {
  if (!ranges || ranges.length === 0) return [blankBlackoutRow()];
  return ranges.map((bod) => ({
    id: getNextBlackoutId(),
    dates: [
      toAdjustedDate(bod?.blackOutStartDate),
      toAdjustedDate(bod?.blackOutEndDate),
    ],
    allowStayThrough: bod?.blackOutStayThruEnabled ?? true,
  }));
};

/** Maps filled blackout rows to the stayVO.blackOutDateRanges API shape. */
const toBlackoutApiPayload = (rows) =>
  rows
    .filter((item) => !!item.dates)
    .map((item) => ({
      blackOutStartDate: formatDate(item.dates[0]),
      blackOutEndDate: formatDate(item.dates[1]),
      blackOutStayThruEnabled: item.allowStayThrough,
    }));

/** Same calendar start and end day; both rows must have a complete pair. */
const sameDatePair = (a, b) =>
  startOfDay(a.dates[0]) === startOfDay(b.dates[0]) &&
  startOfDay(a.dates[1]) === startOfDay(b.dates[1]);

/**
 * Builds the stayDateRanges local-state array from stayVO (EO-1492).
 *
 * Reads stayVO.stayDateRanges when present (new format), otherwise falls back
 * to single stayStartDate/stayEndDate fields (old format). A range whose end
 * date has already passed is marked isLocked=true when in edit mode.
 */
function parseStayDateRanges(stayVO, editingExisting) {
  const todayMs = startOfDay(new Date());

  const apiRanges = stayVO?.stayDateRanges;
  if (apiRanges && apiRanges.length > 0) {
    return apiRanges.map((r, idx) => {
      const start = toAdjustedDate(r.stayStartDate);
      const end = toAdjustedDate(r.stayEndDate);
      const isElapsed = editingExisting && end && startOfDay(end) < todayMs;
      return {
        id: getNextStayId(),
        dates: start && end ? [start, end] : null,
        isLocked: !!isElapsed,
      };
    });
  }

  // Fall back to legacy single fields
  const start = toAdjustedDate(stayVO?.stayStartDate);
  const end = toAdjustedDate(stayVO?.stayEndDate);
  if (start || end) {
    const isElapsed = editingExisting && end && startOfDay(end) < todayMs;
    return [
      {
        id: getNextStayId(),
        dates: start && end ? [start, end] : null,
        isLocked: !!isElapsed,
      },
    ];
  }

  return [{ id: getNextStayId(), dates: null, isLocked: false }];
}

/**
 * Keeps a half-finished Start/End selection alive across a promo re-sync.
 *
 * The split date fields (HTT-9780) emit one end at a time and only write to the
 * promo once both are set, so mid-selection the promo still reads null. Since the
 * parent rebuilds the promo object on every field write, the sync effect reruns
 * immediately and would otherwise reset the pair to [null, null] — discarding the
 * date the user just picked. Only an all-empty incoming pair defers; a promo that
 * actually carries dates still wins, so loading a different promo re-syncs.
 */
const preservePartialPair = (previous, incoming) => {
  const incomingEmpty = !incoming[0] && !incoming[1];
  const previousPartial = !!previous && !!previous[0] !== !!previous[1];
  return incomingEmpty && previousPartial ? previous : incoming;
};

const isCompleteStayRange = (range) => !!range.dates?.[0] && !!range.dates?.[1];

/**
 * Merges promo-synced stay ranges with in-progress local rows (EO-1492).
 *
 * A non-internal promo re-sync (e.g. a booking-window edit) reruns the sync
 * effect. Rebuilding stayDateRanges purely from the promo would drop rows the
 * user has added but not completed: only complete ranges are written to the
 * promo (setFieldForStayDateRanges filters incomplete ones out), so an empty
 * "Add another" row or a half-filled Start/End pair (HTT-9780 split fields)
 * lives only in local state. Keep the promo's complete ranges as the source of
 * truth and re-append the local-only in-progress rows, falling back to a single
 * blank row when nothing remains. Incomplete rows that survive are then flagged
 * by validation, which blocks the save until they are completed or removed.
 *
 * Only rows the user actually created or touched count as in-progress: a row
 * added via "Add another stay date" (isUserAdded) or one with at least one
 * date picked. The blank placeholder parse creates before the promo has
 * loaded carries neither mark, so the edit drawer's async getPromo sync
 * replaces it instead of re-appending it after the saved ranges — otherwise
 * every saved promo would reopen with a phantom empty row.
 */
const mergeSyncedStayRanges = (previous, incoming) => {
  // Rows are keyed by id in the UI, and the promo object is rebuilt on every
  // external field edit (each Name keystroke re-runs this merge) — so a range
  // already on screen keeps its previous row id instead of the freshly minted
  // one, which would remount its pickers. `usedIds` stops two identical
  // ranges (transient; blocked at save by the overlap check) from ending up
  // sharing a key.
  const usedIds = new Set();
  const completeFromPromo = incoming
    .filter(isCompleteStayRange)
    .map((range) => {
      const match = previous.find(
        (prev) =>
          !usedIds.has(prev.id) &&
          isCompleteStayRange(prev) &&
          sameDatePair(prev, range)
      );
      if (!match) return range;
      usedIds.add(match.id);
      return { ...range, id: match.id, isUserAdded: match.isUserAdded };
    });
  const localInProgress = previous.filter(
    (range) =>
      !range.isLocked &&
      !isCompleteStayRange(range) &&
      (range.isUserAdded || range.dates?.[0] || range.dates?.[1])
  );
  const merged = [...completeFromPromo, ...localInProgress];
  if (merged.length > 0) return merged;
  // Reuse the previous blank row when there is one, keeping its id stable
  // across syncs; otherwise fall back to a fresh blank row.
  const previousBlank = previous.find(
    (range) => !range.isLocked && !isCompleteStayRange(range)
  );
  return [
    previousBlank ?? { id: getNextStayId(), dates: null, isLocked: false },
  ];
};

const isFilledBlackoutRow = (row) => !!row.dates?.[0] && !!row.dates?.[1];

/**
 * Blackout twin of mergeSyncedStayRanges. The promo only ever stores filled
 * blackout rows, so a blank "Add another" row — or a blank row whose
 * stay-through toggle was flipped — lives only in local state and must
 * survive an external promo re-sync instead of being silently dropped. The
 * blank placeholder that exists before the edit drawer's getPromo resolves
 * carries no user mark and is replaced by the loaded rows rather than
 * re-appended after them. Filled rows keep their on-screen row id.
 */
const mergeSyncedBlackoutRows = (previous, incoming) => {
  const usedIds = new Set();
  const filledFromPromo = incoming.filter(isFilledBlackoutRow).map((row) => {
    const match = previous.find(
      (prev) =>
        !usedIds.has(prev.id) &&
        isFilledBlackoutRow(prev) &&
        sameDatePair(prev, row)
    );
    if (!match) return row;
    usedIds.add(match.id);
    return { ...row, id: match.id, isUserAdded: match.isUserAdded };
  });
  const localInProgress = previous.filter(
    (row) =>
      !isFilledBlackoutRow(row) &&
      (row.isUserAdded || row.allowStayThrough === false)
  );
  const merged = [...filledFromPromo, ...localInProgress];
  if (merged.length > 0) return merged;
  const previousBlank = previous.find((row) => !isFilledBlackoutRow(row));
  return [previousBlank ?? blankBlackoutRow()];
};

/**
 * Sub-hook of `useTargetingConditionsState` owning booking-window,
 * promotion-stay-date (PSD), and blackout date-range state, handlers,
 * and validation effects.
 *
 * Exposes the same field names the parent orchestrator used to expose
 * directly so existing consumers continue to work unchanged. Stay dates are
 * multi-range (EO-1492): `stayDateRanges` + per-range handlers/errors.
 * Note: Uses per-date timezone offsets to handle DST correctly (EO-299).
 */
export function useTcDateRanges({
  promo,
  tc,
  setTc,
  handleFieldChange,
  handleInternalFieldChange,
  setIsNextBtnDisabled,
  isInternalUpdateRef,
  // Editing an existing promo: a locked past start date is not an error the
  // user can fix, so past-date validation is relaxed (HTT-9780).
  isEditingExistingPromo = false,
}) {
  // Initial date getters - use DST-aware helpers from targetingConditionsInitial
  const getInitialBookingDates = useCallback(() => {
    return getInitialBookingDatesHelper({ promo });
  }, [promo]);

  const getInitialStayDateRanges = useCallback(() => {
    return parseStayDateRanges(
      promo?.rateRule?.ruleVO?.stayVO,
      isEditingExistingPromo
    );
  }, [promo, isEditingExistingPromo]);

  const getInitialBlackoutDates = useCallback(
    () =>
      parseBlackoutDates(promo?.rateRule?.ruleVO?.stayVO?.blackOutDateRanges),
    [promo]
  );

  // State
  const [bookingDates, setBookingDates] = useState(getInitialBookingDates);
  const [stayDateRanges, setStayDateRanges] = useState(
    getInitialStayDateRanges
  );
  const [blackoutDates, setBlackoutDates] = useState(getInitialBlackoutDates);

  // Validation error states
  const [stayDateRangeErrors, setStayDateRangeErrors] = useState({});
  const [hasBookingDateError, setHasBookingDateError] = useState(false);

  // Derived values
  const fromBooking = bookingDates[0];
  const toBooking = bookingDates[1];

  // Earliest non-locked stay start (for blackout validation and booking-order check)
  const earliestStayStart = getEarliestStayStart(stayDateRanges);

  // Sync state when promo changes. Booking dates always re-derive from the
  // incoming promo (a half-filled split-field pair survives an all-empty
  // incoming promo — see preservePartialPair). Stay ranges + blackouts only
  // re-parse when the promo update came from outside this hook tree —
  // `isInternalUpdateRef` is owned by the parent orchestrator and flipped to
  // true inside `handleInternalFieldChange`. The parent clears the flag in its
  // own sync effect, which fires AFTER this one (effects run in declaration
  // order), so this hook reads the flag while it is still set.
  useEffect(() => {
    const stayVO = promo?.rateRule?.ruleVO?.stayVO;

    setBookingDates((prev) =>
      preservePartialPair(prev, getInitialBookingDatesHelper({ promo }))
    );

    if (!isInternalUpdateRef?.current) {
      const incomingStayRanges = parseStayDateRanges(
        stayVO,
        isEditingExistingPromo
      );
      setStayDateRanges((prev) =>
        mergeSyncedStayRanges(prev, incomingStayRanges)
      );
      setBlackoutDates((prev) =>
        mergeSyncedBlackoutRows(
          prev,
          parseBlackoutDates(stayVO?.blackOutDateRanges)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo]);

  // Write stayDateRanges back to the promo, keeping legacy single fields in sync.
  const setFieldForStayDateRanges = useCallback(
    (ranges) => {
      const rangesForApi = ranges
        .filter((r) => r.dates?.[0] && r.dates?.[1])
        .map((r) => ({
          stayStartDate: formatDate(r.dates[0]),
          stayEndDate: formatDate(r.dates[1]),
        }));
      handleInternalFieldChange(
        "rateRule.ruleVO.stayVO.stayDateRanges",
        rangesForApi
      );
      // Backward compat: keep single fields from first range
      const first = rangesForApi[0];
      handleInternalFieldChange(
        "rateRule.ruleVO.stayVO.stayStartDate",
        first?.stayStartDate ?? null
      );
      handleInternalFieldChange(
        "rateRule.ruleVO.stayVO.stayEndDate",
        first?.stayEndDate ?? null
      );
    },
    [handleInternalFieldChange]
  );

  // Writes blackout rows back to the promo (filled rows only).
  const setFieldForBlackoutDates = useCallback(
    (rows) => {
      handleInternalFieldChange(
        "rateRule.ruleVO.stayVO.blackOutDateRanges",
        toBlackoutApiPayload(rows)
      );
    },
    [handleInternalFieldChange]
  );

  // Booking window handlers
  const setShowBooking = useCallback(() => {
    if (tc.showBooking) {
      setBookingDates([null, null]);
      handleFieldChange("rateRule.ruleVO.bookingVO.bookingStartDate", null);
      handleFieldChange("rateRule.ruleVO.bookingVO.bookingEndDate", null);
    } else {
      const [from, to] = getInitialBookingDates();
      setBookingDates([from, to]);
      if (from && to) {
        handleFieldChange(
          "rateRule.ruleVO.bookingVO.bookingStartDate",
          formatDate(from)
        );
        handleFieldChange(
          "rateRule.ruleVO.bookingVO.bookingEndDate",
          formatDate(to)
        );
      }
    }
    setTc({ ...tc, showBooking: !tc.showBooking });
  }, [tc, setTc, handleFieldChange, getInitialBookingDates]);

  const handleBookingChange = useCallback(
    (value) => {
      // Split Start/End fields (HTT-9780) emit one end at a time, so a half-filled
      // pair has to survive in local state — dropping it would make the first pick
      // vanish. Only a complete range is written to the promo.
      const [start, end] = value ?? [null, null];
      const complete = !!start && !!end;
      setBookingDates([start ?? null, end ?? null]);
      handleFieldChange(
        "rateRule.ruleVO.bookingVO.bookingStartDate",
        complete ? formatDate(start) : null
      );
      handleFieldChange(
        "rateRule.ruleVO.bookingVO.bookingEndDate",
        complete ? formatDate(end) : null
      );
    },
    [handleFieldChange]
  );

  // Stay dates handlers (EO-1492 — multi-range)
  const setShowPSD = useCallback(() => {
    if (tc.showPSD) {
      // Hiding: clear all stay date state
      const emptyRanges = [];
      setStayDateRanges([
        { id: getNextStayId(), dates: null, isLocked: false },
      ]);
      handleInternalFieldChange(
        "rateRule.ruleVO.stayVO.stayDateRanges",
        emptyRanges
      );
      handleInternalFieldChange("rateRule.ruleVO.stayVO.stayStartDate", null);
      handleInternalFieldChange("rateRule.ruleVO.stayVO.stayEndDate", null);
    } else {
      // Showing: restore from promo or start with one blank row
      const initial = getInitialStayDateRanges();
      setStayDateRanges(initial);
      const hasReal = initial.some((r) => r.dates?.[0] && r.dates?.[1]);
      if (hasReal) {
        setFieldForStayDateRanges(initial);
      }
    }
    setTc({ ...tc, showPSD: !tc.showPSD });
  }, [
    tc,
    setTc,
    handleInternalFieldChange,
    getInitialStayDateRanges,
    setFieldForStayDateRanges,
  ]);

  const handleStayDateRangeChange = useCallback(
    (value, id) => {
      // The split Start/End fields (HTT-9780) emit one end at a time, so a
      // half-filled pair has to survive in local state — dropping it would make
      // the first pick vanish. `setFieldForStayDateRanges` already filters
      // incomplete ranges out of the promo payload, so only complete ranges are
      // sent. The DateRangePicker (create mode) always emits a complete pair.
      const [start, end] = value ?? [null, null];
      const dates = start || end ? [start ?? null, end ?? null] : null;
      const updated = stayDateRanges.map((item) =>
        item.id === id ? { ...item, dates } : item
      );
      setStayDateRanges(updated);
      setFieldForStayDateRanges(updated);
    },
    [stayDateRanges, setFieldForStayDateRanges]
  );

  const addStayDateRange = useCallback(() => {
    setStayDateRanges([
      ...stayDateRanges,
      // isUserAdded keeps this row alive across promo re-syncs — unlike the
      // blank placeholder rows parse creates (see mergeSyncedStayRanges).
      { id: getNextStayId(), dates: null, isLocked: false, isUserAdded: true },
    ]);
  }, [stayDateRanges]);

  const removeStayDateRange = useCallback(
    (id) => {
      const remaining = stayDateRanges.filter((r) => r.id !== id);
      // The section never shows zero rows: deleting the last range resets to
      // a single blank (evergreen) row, mirroring removeBlackoutDate.
      const next =
        remaining.length > 0
          ? remaining
          : [{ id: getNextStayId(), dates: null, isLocked: false }];
      setStayDateRanges(next);
      setFieldForStayDateRanges(next);
    },
    [stayDateRanges, setFieldForStayDateRanges]
  );

  // Blackout dates handlers
  const setShowBlackouts = useCallback(() => {
    if (tc.showBlackouts) {
      handleInternalFieldChange(
        "rateRule.ruleVO.stayVO.blackOutDateRanges",
        []
      );
    } else {
      setFieldForBlackoutDates(blackoutDates);
    }
    setTc({ ...tc, showBlackouts: !tc.showBlackouts });
  }, [
    tc,
    setTc,
    handleInternalFieldChange,
    blackoutDates,
    setFieldForBlackoutDates,
  ]);

  const setBODates = useCallback(
    (value, id) => {
      if (!value) {
        const dates = blackoutDates.filter((item) => item.id !== id);
        setFieldForBlackoutDates(dates);
        setBlackoutDates(dates);
        return;
      }
      if (value[0] === null || value[1] === null) return;
      if (fromBooking || earliestStayStart) {
        if (value[0] < fromBooking || value[0] < earliestStayStart) {
          toast.error(
            "Blackout start date cannot be before booking start date or stay start date"
          );
          setIsNextBtnDisabled(true);
          return;
        }
      }
      const dates = blackoutDates.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          dates: value ? [new Date(value[0]), new Date(value[1])] : null,
        };
      });

      setIsNextBtnDisabled(false);
      setFieldForBlackoutDates(dates);
      setBlackoutDates(dates);
    },
    [
      blackoutDates,
      fromBooking,
      earliestStayStart,
      setIsNextBtnDisabled,
      setFieldForBlackoutDates,
    ]
  );

  const addBlackoutDate = useCallback(() => {
    setBlackoutDates([
      ...blackoutDates,
      // isUserAdded keeps this row alive across promo re-syncs — unlike the
      // blank placeholder rows parse creates (see mergeSyncedBlackoutRows).
      { ...blankBlackoutRow(), isUserAdded: true },
    ]);
  }, [blackoutDates]);

  const removeBlackoutDate = useCallback(
    (id) => {
      const newBlackoutDates = blackoutDates.filter((item) => item.id !== id);
      if (newBlackoutDates.length === 0) {
        setBlackoutDates([blankBlackoutRow()]);
        setTc({ ...tc, showBlackouts: false });
        handleInternalFieldChange(
          "rateRule.ruleVO.stayVO.blackOutDateRanges",
          []
        );
      } else {
        setBlackoutDates(newBlackoutDates);
        setFieldForBlackoutDates(newBlackoutDates);
      }
    },
    [
      blackoutDates,
      tc,
      setTc,
      handleInternalFieldChange,
      setFieldForBlackoutDates,
    ]
  );

  const handleAllowStayThrough = useCallback(
    (id) => {
      const updatedDates = blackoutDates.map((item) => {
        if (item.id !== id) return item;
        return { ...item, allowStayThrough: !item.allowStayThrough };
      });
      setBlackoutDates(updatedDates);
      setFieldForBlackoutDates(updatedDates);
    },
    [blackoutDates, setFieldForBlackoutDates]
  );

  // Effect to update per-range stay date error states in real-time
  useEffect(() => {
    if (!tc.showPSD) {
      setStayDateRangeErrors({});
      return;
    }
    const todayMs = startOfDay(new Date());
    const errors = {};
    stayDateRanges.forEach((item) => {
      if (item.isLocked) return;
      const startVal = item.dates?.[0];
      const endVal = item.dates?.[1];
      // Half-filled range (exactly one end chosen) — flag inline so the user
      // completes it. A fully blank row waits for the save-time check instead
      // of showing red before any input (EO-1492).
      if (!!startVal !== !!endVal) {
        errors[item.id] = true;
        return;
      }
      if (!startVal) return;
      const startMs = startOfDay(startVal);
      let hasError = startMs < todayMs && !isEditingExistingPromo;

      if (tc.showBooking && fromBooking && !hasError) {
        const bookingStartMs = startOfDay(fromBooking);
        const bothLocked =
          isEditingExistingPromo &&
          startMs < todayMs &&
          bookingStartMs < todayMs;
        hasError = startMs < bookingStartMs && !bothLocked;
      }

      if (hasError) errors[item.id] = true;
    });
    setStayDateRangeErrors(errors);
  }, [
    tc.showPSD,
    tc.showBooking,
    stayDateRanges,
    fromBooking,
    isEditingExistingPromo,
  ]);

  // Effect to update booking date error state in real-time
  useEffect(() => {
    if (tc.showBooking && fromBooking) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingStart = new Date(fromBooking);
      bookingStart.setHours(0, 0, 0, 0);
      setHasBookingDateError(
        bookingStart.getTime() < today.getTime() && !isEditingExistingPromo
      );
    } else {
      setHasBookingDateError(false);
    }
  }, [tc.showBooking, fromBooking, isEditingExistingPromo]);

  const hasStayDateError = Object.keys(stayDateRangeErrors).length > 0;

  return {
    // Booking window
    fromBooking,
    toBooking,
    setShowBooking,
    handleBookingChange,

    // Stay dates (EO-1492 multi-range)
    stayDateRanges,
    setShowPSD,
    handleStayDateRangeChange,
    addStayDateRange,
    removeStayDateRange,
    stayDateRangeErrors,

    // Blackout dates
    blackoutDates,
    setShowBlackouts,
    setBODates,
    addBlackoutDate,
    removeBlackoutDate,
    handleAllowStayThrough,

    // Validation
    hasStayDateError,
    hasBookingDateError,
  };
}
